import {
  ComboBox,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
} from "@heroui/react";

export interface NamedOption {
  id: string;
  name: string;
}

/** A searchable list of named options, opened on focus. An empty selection is the empty string. */
export const ComboField = ({
  label,
  hideLabel,
  inline,
  items,
  value,
  placeholder,
  emptyText = "No matches",
  isRequired,
  isInvalid,
  errorMessage,
  description,
  allowsCustomValue,
  inputValue,
  onInputChange,
  disableFilter,
  onBlur,
  onChange,
}: {
  label: string;
  /** Keep the label for assistive tech only (a field inside a table row). */
  hideLabel?: boolean;
  /** A quiet look for a field in a table row: no fill or border until hover or focus, as wide as its value. */
  inline?: boolean;
  items: NamedOption[];
  value: string;
  placeholder?: string;
  emptyText?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  description?: string;
  allowsCustomValue?: boolean;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  /** For options that were already searched on the server. */
  disableFilter?: boolean;
  onBlur?: () => void;
  onChange: (id: string) => void;
}) => (
  <ComboBox
    allowsCustomValue={allowsCustomValue}
    className={inline ? "flex w-fit flex-col" : "flex flex-col gap-1.5"}
    defaultFilter={disableFilter ? () => true : undefined}
    inputValue={inputValue}
    isInvalid={isInvalid}
    isRequired={isRequired}
    items={items}
    menuTrigger="focus"
    selectedKey={value || null}
    variant="secondary"
    onBlur={onBlur}
    onInputChange={onInputChange}
    onSelectionChange={(key) => onChange(key === null ? "" : String(key))}
  >
    <Label className={hideLabel ? "sr-only" : undefined}>{label}</Label>
    <ComboBox.InputGroup
      className={
        inline
          ? "hover:bg-surface-secondary! focus-within:bg-surface-secondary! bg-transparent! shadow-none!"
          : undefined
      }
    >
      <Input
        className={
          inline
            ? "field-sizing-content min-w-24 bg-transparent! shadow-none!"
            : undefined
        }
        placeholder={placeholder}
      />
      <ComboBox.Trigger />
    </ComboBox.InputGroup>
    {description ? <Description>{description}</Description> : null}
    <FieldError>{errorMessage}</FieldError>
    <ComboBox.Popover>
      <ListBox
        renderEmptyState={() => (
          <p className="text-muted px-3 py-2 text-sm">{emptyText}</p>
        )}
      >
        {(item: NamedOption) => (
          <ListBox.Item id={item.id} textValue={item.name}>
            {item.name}
            <ListBox.ItemIndicator />
          </ListBox.Item>
        )}
      </ListBox>
    </ComboBox.Popover>
  </ComboBox>
);
